import { useEffect, useState, useCallback, useMemo } from "react";
import { Box, Flex, Heading, Text, Button, Input, Stack, HStack, Badge, Icon, Spinner, Wrap } from "@chakra-ui/react";
import { MapPin, Users, UserPlus, Plus, Pencil, Mail, CheckCircle2, Settings2, Trash2 } from "lucide-react";
import { supabase } from "@/lib/supabaseClient";
import { apiPost } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Modal } from "@/components/Modal";
import { ConfirmModal } from "@/components/ConfirmModal";
import { inputStyle } from "@/pages/auth/AuthShell";
import { SegmentedTabs } from "@/components/SegmentedTabs";
import { BulkInviteModal } from "@/components/BulkInvite";
import { WorkerPicker } from "@/components/WorkerPicker";
import { colors } from "@/theme/colors";

const TABS = [
  { key: "locations", label: "Locations", icon: MapPin },
  { key: "employees", label: "Employees", icon: Users },
  { key: "pending", label: "Pending Users", icon: UserPlus },
];

// Org Admins invite these three role types.
const INVITE_ROLES = [
  { value: "org_admin", label: "Org Admin" },
  { value: "supervisor", label: "Supervisor" },
  { value: "field_employee", label: "Employee" },
];
const EMP_ROLE_LABELS = { org_admin: "Org Admin", supervisor: "Supervisor", field_employee: "Employee" };

const Banner = ({ msg }) =>
  msg ? (
    <Box mb="4" px="4" py="2.5" borderRadius="10px" fontSize="sm"
      bg={msg.type === "error" ? colors.criticalBg : colors.resolvedBg}
      color={msg.type === "error" ? colors.criticalText : colors.resolvedText}
      data-testid="setup-banner">
      {msg.text}
    </Box>
  ) : null;

const Card = ({ children, testId }) => (
  <Box bg={colors.white} border="1px solid" borderColor={colors.border} borderRadius="12px" p="4" data-testid={testId}>
    {children}
  </Box>
);

const FieldRow = ({ label, ...rest }) => (
  <Box>
    <Text fontSize="sm" fontWeight="500" mb="1.5" color={colors.textPrimary}>{label}</Text>
    <Input {...inputStyle} {...rest} />
  </Box>
);

const RoleSelect = ({ value, onChange, testId }) => (
  <Box as="select" value={value} onChange={onChange} data-testid={testId}
    w="full" px="3" py="2" borderRadius="8px" border="1px solid" borderColor={colors.border} bg={colors.white} fontSize="sm">
    {INVITE_ROLES.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
  </Box>
);

// A clickable pill used for multi-selecting locations / supervised employees.
const Pill = ({ active, onClick, children, testId }) => (
  <Button size="xs" onClick={onClick} data-testid={testId} type="button"
    bg={active ? colors.teal : colors.white} color={active ? "white" : colors.textPrimary} borderColor={active ? colors.teal : colors.border}
    _hover={{ bg: active ? colors.tealHover : colors.cream }}>
    {children}
  </Button>
);

// Active / Deactivated declutter segments — mirrors the Companies tab.
const StatusSegments = ({ value, onChange, activeCount, deactivatedCount, testPrefix }) => (
  <SegmentedTabs value={value} onChange={onChange} tabs={[
    { key: "active", label: `Active ${activeCount}`, testId: `${testPrefix}-seg-active` },
    { key: "deactivated", label: `Deactivated ${deactivatedCount}`, testId: `${testPrefix}-seg-deactivated` },
  ]} />
);

export default function Setup({ overrideCompanyId = null, overrideCompanyName = null, initialTab = "locations" }) {
  const { activeCompanyId, companyName } = useAuth();
  const companyId = overrideCompanyId || activeCompanyId;
  const cName = overrideCompanyId ? overrideCompanyName : companyName;
  const [tab, setTab] = useState(initialTab);
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState(null);
  const [locations, setLocations] = useState([]);
  const [workers, setWorkers] = useState([]);
  const [memberships, setMemberships] = useState([]);
  const [pending, setPending] = useState([]);
  const [workerLocs, setWorkerLocs] = useState([]);
  const [supWorkers, setSupWorkers] = useState([]);
  const [deactInfo, setDeactInfo] = useState({ workers: {}, locations: {} });
  const [profilesById, setProfilesById] = useState({});

  const flash = (type, text) => { setMsg({ type, text }); if (type !== "error") setTimeout(() => setMsg(null), 4500); };

  const load = useCallback(async (background = false) => {
    if (!companyId) { setLoading(false); return; }
    if (!background) setLoading(true);
    const [{ data: locs }, { data: wks }, { data: mems }] = await Promise.all([
      supabase.from("locations").select("*").eq("company_id", companyId).order("created_at", { ascending: false }),
      supabase.from("workers").select("*").eq("company_id", companyId).order("created_at", { ascending: false }),
      supabase.from("memberships").select("id,user_id,role,status").eq("company_id", companyId),
    ]);
    setLocations(locs || []);
    setWorkers(wks || []);
    setMemberships(mems || []);

    const workerIds = (wks || []).map((w) => w.id);
    const [{ data: wl }, { data: sw }] = await Promise.all([
      workerIds.length ? supabase.from("worker_locations").select("*").in("worker_id", workerIds) : Promise.resolve({ data: [] }),
      workerIds.length ? supabase.from("supervisor_workers").select("*").in("supervisor_id", workerIds) : Promise.resolve({ data: [] }),
    ]);
    setWorkerLocs(wl || []);
    setSupWorkers(sw || []);

    // Profiles for everyone linked to a login — the LOGIN email is the single source of
    // truth shown for linked workers; workers.email only matters pre-invite.
    const pend = (mems || []).filter((m) => m.status === "pending");
    const ids = [...new Set([
      ...pend.map((m) => m.user_id),
      ...(wks || []).map((w) => w.profile_id).filter(Boolean),
    ])];
    let profs = [];
    if (ids.length) {
      const { data } = await supabase.from("profiles").select("id,full_name,email").in("id", ids);
      profs = data || [];
    }
    setProfilesById(Object.fromEntries(profs.map((p) => [p.id, p])));
    setPending(pend.map((m) => ({
      ...m,
      profile: profs.find((p) => p.id === m.user_id),
      worker: (wks || []).find((w) => w.profile_id === m.user_id),
    })));

    // Accountability line for Deactivated segments: when + by whom (from the immutable audit trail).
    const { data: audit } = await supabase.from("audit_log")
      .select("action,details,actor,created_at")
      .eq("company_id", companyId)
      .in("action", ["user_deactivated", "location_deactivated"])
      .order("created_at", { ascending: false }).limit(300);
    const actorIds = [...new Set((audit || []).map((a) => a.actor).filter(Boolean))];
    const actorNames = {};
    if (actorIds.length) {
      const { data: actors } = await supabase.from("profiles").select("id,full_name,email").in("id", actorIds);
      (actors || []).forEach((p) => { actorNames[p.id] = p.full_name || p.email; });
    }
    const fmt = (ts) => new Date(ts).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
    const wMap = {}, lMap = {};
    (audit || []).forEach((a) => {
      const by = actorNames[a.actor] || null;
      if (a.action === "location_deactivated") {
        const id = a.details?.location_id;
        if (id && !lMap[id]) lMap[id] = { when: fmt(a.created_at), by };
      } else {
        const wid = a.details?.worker_id || (wks || []).find((w) => w.profile_id && w.profile_id === a.details?.target_user)?.id;
        if (wid && !wMap[wid]) wMap[wid] = { when: fmt(a.created_at), by };
      }
    });
    setDeactInfo({ workers: wMap, locations: lMap });
    if (!background) setLoading(false);
  }, [companyId]);

  useEffect(() => { load(); }, [load]);

  if (!companyId) {
    return (
      <Box data-testid="setup-page">
        <Heading fontFamily="heading" fontWeight="700" fontSize={{ base: "2xl", md: "3xl" }} mb="2">Locations & Employees</Heading>
        <Text color={colors.textSecondary}>This screen is for company Org Admins. Super-Admins manage tenants from the "Companies & Users" screen.</Text>
      </Box>
    );
  }

  const membByUser = {};
  memberships.forEach((m) => { membByUser[m.user_id] = m; });

  return (
    <Box data-testid="setup-page">
      <Heading fontFamily="heading" fontWeight="700" fontSize={{ base: "2xl", md: "3xl" }} color={colors.textPrimary}>Locations & Employees</Heading>
      <Text color={colors.textSecondary} mb="5">{cName}</Text>

      <Box mb="5">
        <SegmentedTabs value={tab} onChange={setTab}
          tabs={TABS.map((t) => ({
            ...t,
            badge: t.key === "pending" && pending.length > 0
              ? <Badge bg={colors.poppyTint} color={colors.poppy} borderRadius="full" px="2">{pending.length}</Badge>
              : null,
          }))} />
      </Box>

      <Banner msg={msg} />

      {loading ? (
        <Flex minH="30vh" align="center" justify="center"><Spinner color={colors.teal} borderWidth="3px" /></Flex>
      ) : tab === "locations" ? (
        <LocationsTab companyId={companyId} locations={locations} deactInfo={deactInfo.locations} reload={() => load(true)} flash={flash} />
      ) : tab === "employees" ? (
        <EmployeesTab companyId={companyId} workers={workers} membByUser={membByUser}
          locations={locations} workerLocs={workerLocs} supWorkers={supWorkers} deactInfo={deactInfo.workers}
          profilesById={profilesById} reload={() => load(true)} flash={flash} />
      ) : (
        <PendingTab pending={pending} reload={() => load(true)} flash={flash} />
      )}
    </Box>
  );
}

// ---------------- Locations ----------------
function LocationsTab({ companyId, locations, deactInfo = {}, reload, flash }) {
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", address: "", site_contact: "" });
  const [saving, setSaving] = useState(false);

  const openAdd = () => { setEditing(null); setForm({ name: "", address: "", site_contact: "" }); setOpen(true); };
  const openEdit = (l) => { setEditing(l); setForm({ name: l.name, address: l.address || "", site_contact: l.site_contact || "" }); setOpen(true); };

  const save = async () => {
    setSaving(true);
    const clean = { ...form, name: form.name.trim(), address: form.address.trim(), site_contact: form.site_contact.trim() };
    try {
      if (editing) {
        const { error } = await supabase.from("locations").update(clean).eq("id", editing.id);
        if (error) throw error;
        flash("success", "Location updated.");
      } else {
        const { error } = await supabase.from("locations").insert({ ...clean, company_id: companyId });
        if (error) throw error;
        flash("success", "Location added.");
      }
      setOpen(false);
      reload();
    } catch (e) { flash("error", e.message); } finally { setSaving(false); }
  };

  const [confirmLoc, setConfirmLoc] = useState(null); // location pending deactivation
  const [delLoc, setDelLoc] = useState(null); // location pending permanent delete
  const [deleting, setDeleting] = useState(false);
  const [seg, setSeg] = useState("active");

  const activeLocs = locations.filter((l) => l.active);
  const inactiveLocs = locations.filter((l) => !l.active);
  const shown = seg === "active" ? activeLocs : inactiveLocs;

  // Delete is only OFFERED for no-history sites — everything else gets Deactivate only.
  const [deletable, setDeletable] = useState(new Set());
  const inactiveIds = inactiveLocs.map((l) => l.id).sort().join(",");
  useEffect(() => {
    if (!inactiveIds) { setDeletable(new Set()); return; }
    apiPost("/setup/deletable-check", { location_ids: inactiveIds.split(",") })
      .then((r) => setDeletable(new Set(r.locations)))
      .catch(() => setDeletable(new Set()));
  }, [inactiveIds]);

  const setActive = async (l, active) => {
    const { error } = await supabase.from("locations").update({ active }).eq("id", l.id);
    if (error) flash("error", error.message);
    else { flash("success", active ? `${l.name} reactivated.` : `${l.name} deactivated.`); setConfirmLoc(null); reload(); }
  };

  const deleteLoc = async () => {
    setDeleting(true);
    try {
      await apiPost(`/locations/${delLoc.id}/delete`, {});
      flash("success", `${delLoc.name} permanently deleted.`);
      setDelLoc(null);
      reload();
    } catch (e) {
      setDelLoc(null);
      flash("error", e.message);
    } finally { setDeleting(false); }
  };

  return (
    <Stack gap="3">
      <Flex justify="space-between" align="center" gap="2" flexWrap="wrap">
        <StatusSegments value={seg} onChange={setSeg} activeCount={activeLocs.length}
          deactivatedCount={inactiveLocs.length} testPrefix="locations" />
        <Button size="sm" onClick={openAdd} data-testid="add-location-btn">
          <Plus size={16} /> Add location
        </Button>
      </Flex>
      {shown.length === 0 ? (
        <Card testId="locations-empty">
          <Text color={colors.textSecondary} fontSize="sm">
            {seg === "active" ? "No job sites yet. Add your first location." : "No deactivated locations."}
          </Text>
        </Card>
      ) : (
        shown.map((l) => (
          <Card key={l.id} testId={`location-row-${l.id}`}>
            <Flex justify="space-between" align="flex-start" gap="3" flexWrap="wrap">
              <Box>
                <HStack gap="2">
                  <Text fontWeight="600" color={colors.textPrimary}>{l.name}</Text>
                  <Badge bg={l.active ? colors.resolvedBg : colors.pendingBg} color={l.active ? colors.resolvedText : colors.pendingText} borderRadius="full" px="2.5">{l.active ? "Active" : "Deactivated"}</Badge>
                </HStack>
                {l.address && <Text fontSize="sm" color={colors.textSecondary}>{l.address}</Text>}
                {l.site_contact && <Text fontSize="sm" color={colors.textSecondary}>Contact: {l.site_contact}</Text>}
                {!l.active && deactInfo[l.id] && (
                  <Text fontSize="xs" color={colors.textSecondary} mt="1" data-testid={`deact-info-${l.id}`}>
                    Deactivated {deactInfo[l.id].when}{deactInfo[l.id].by ? ` by ${deactInfo[l.id].by}` : ""}
                  </Text>
                )}
              </Box>
              <HStack gap="2">
                <Button size="xs" variant="outline" onClick={() => openEdit(l)} data-testid={`edit-location-${l.id}`}><Pencil size={14} /> Edit</Button>
                {l.active ? (
                  <Button size="xs" variant="outline" color={colors.poppy} onClick={() => setConfirmLoc(l)} data-testid={`toggle-location-${l.id}`}>Deactivate</Button>
                ) : (
                  <>
                    <Button size="xs" variant="outline" color={colors.sageDeep} onClick={() => setActive(l, true)} data-testid={`reactivate-location-${l.id}`}>Reactivate</Button>
                    {deletable.has(l.id) && (
                      <Button size="xs" variant="outline" color={colors.poppy} onClick={() => setDelLoc(l)} data-testid={`delete-location-${l.id}`}><Trash2 size={13} /> Delete</Button>
                    )}
                  </>
                )}
              </HStack>
            </Flex>
          </Card>
        ))
      )}

      <ConfirmModal open={!!confirmLoc} onClose={() => setConfirmLoc(null)}
        title={confirmLoc ? `Deactivate ${confirmLoc.name}?` : ""}
        body="The site moves to the Deactivated list and stops appearing in form pickers. Nothing is deleted — you can reactivate it at any time."
        confirmLabel="Deactivate" danger onConfirm={() => setActive(confirmLoc, false)} testId="location-confirm-modal" />

      <ConfirmModal open={!!delLoc} onClose={() => setDelLoc(null)}
        title={delLoc ? `Permanently delete ${delLoc.name}?` : ""}
        body="This site has no submissions or scheduled forms tagged to it, so it can be removed permanently. This cannot be undone."
        confirmLabel="Delete" danger busy={deleting} onConfirm={deleteLoc} testId="location-delete-modal" />

      <Modal open={open} onClose={() => setOpen(false)} title={editing ? "Edit location" : "Add location"} testId="location-modal">
        <Stack gap="4">
          <FieldRow label="Site name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="location-name-input" />
          <FieldRow label="Address" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} data-testid="location-address-input" />
          <FieldRow label="Site contact (optional)" value={form.site_contact} onChange={(e) => setForm({ ...form, site_contact: e.target.value })} data-testid="location-contact-input" />
          <Button onClick={save} loading={saving} disabled={!form.name} data-testid="save-location-btn">Save</Button>
        </Stack>
      </Modal>
    </Stack>
  );
}

// ---------------- Employees ----------------
function EmployeesTab({ companyId, workers, membByUser, locations, workerLocs, supWorkers, deactInfo = {}, profilesById = {}, reload, flash }) {
  const [addOpen, setAddOpen] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [form, setForm] = useState({ full_name: "", job_role: "", email: "", phone: "", role: "field_employee" });
  const [saving, setSaving] = useState(false);
  const [inviteLink, setInviteLink] = useState(null);
  const [inviting, setInviting] = useState(null);
  const [manage, setManage] = useState(null);
  const [addError, setAddError] = useState(null);

  const membFor = (w) => (w.profile_id ? membByUser[w.profile_id] : null);
  // Employees bucket = everyone except those still pending first login.
  const employees = workers.filter((w) => { const m = membFor(w); return !m || m.status !== "pending"; });
  const [seg, setSeg] = useState("active");
  const [reactivating, setReactivating] = useState(null);
  const [delEmp, setDelEmp] = useState(null); // employee pending permanent delete
  const [deleting, setDeleting] = useState(false);
  const activeEmployees = employees.filter((w) => w.status !== "inactive");
  const deactivatedEmployees = employees.filter((w) => w.status === "inactive");
  const shown = seg === "active" ? activeEmployees : deactivatedEmployees;

  // Delete is only OFFERED for no-history people — everyone else gets Deactivate only.
  const [deletable, setDeletable] = useState(new Set());
  const candidateIds = employees
    .filter((w) => w.status === "inactive" || !membFor(w))
    .map((w) => w.id).sort().join(",");
  useEffect(() => {
    if (!candidateIds) { setDeletable(new Set()); return; }
    apiPost("/setup/deletable-check", { worker_ids: candidateIds.split(",") })
      .then((r) => setDeletable(new Set(r.workers)))
      .catch(() => setDeletable(new Set()));
  }, [candidateIds]);

  const reactivate = async (w) => {
    setReactivating(w.id);
    try {
      await apiPost(`/people/${w.id}/status`, { active: true });
      flash("success", `${w.full_name} reactivated — they're back in assignment pickers.`);
      reload();
    } catch (e) { flash("error", e.message); } finally { setReactivating(null); }
  };

  // Guarded, no-history delete — the server refuses anyone with records.
  const deleteEmp = async () => {
    setDeleting(true);
    try {
      await apiPost(`/people/${delEmp.id}/delete`, {});
      flash("success", `${delEmp.full_name} permanently deleted — worker record and login removed.`);
      setDelEmp(null);
      reload();
    } catch (e) {
      setDelEmp(null);
      flash("error", e.message);
    } finally { setDeleting(false); }
  };
  const managedLocIds = useMemo(
    () => (manage ? workerLocs.filter((x) => x.worker_id === manage.id).map((x) => x.location_id) : []),
    [workerLocs, manage]
  );
  const managedSupIds = useMemo(
    () => (manage ? supWorkers.filter((x) => x.supervisor_id === manage.id).map((x) => x.worker_id) : []),
    [supWorkers, manage]
  );

  const doInvite = async (email, worker_id, role) => {
    const res = await apiPost("/workers/invite", {
      email, company_id: companyId, worker_id, role,
      redirect_to: `${window.location.origin}/accept-invite`,
    });
    return res.hashed_token
      ? { link: `${window.location.origin}/accept-invite?token_hash=${res.hashed_token}&type=recovery`, emailed: res.emailed }
      : null;
  };

  // Unconditional reset on close — the next record must never inherit the previous one.
  const closeAdd = () => {
    setAddOpen(false);
    setForm({ full_name: "", job_role: "", email: "", phone: "", role: "field_employee" });
    setAddError(null);
  };

  const addEmployee = async () => {
    if (!form.email) { setAddError("Email is required to invite the employee."); return; }
    setSaving(true);
    setAddError(null);
    let wk = null;
    try {
      const roleLabel = EMP_ROLE_LABELS[form.role];
      const { data, error } = await supabase.from("workers")
        .insert({ full_name: form.full_name, job_role: form.job_role, email: form.email.trim().toLowerCase(), phone: form.phone.trim() || null, company_id: companyId })
        .select().single();
      if (error) throw error;
      wk = data;
      const invite = await doInvite(wk.email, wk.id, form.role);
      closeAdd();
      if (invite?.link) setInviteLink({ email: wk.email, link: invite.link, emailed: invite.emailed });
      flash("success", `${wk.full_name} added as ${roleLabel}${invite?.emailed ? " — invite email sent" : ""}. ${invite?.emailed ? "A copyable link is below as backup." : "Share the invite link below."}`);
      reload();
    } catch (e) {
      // Invite guard blocked it — roll back the just-created worker so nothing is left behind.
      if (wk && e.detail?.code) await supabase.from("workers").delete().eq("id", wk.id);
      // Inline, non-dismissing while the modal is open — never a banner behind the modal.
      setAddError(e.code === "23505" || (e.message || "").includes("uq_workers_company_email")
        ? "Another employee in this company already has this email. Edit that record instead of adding a new one."
        : e.message);
    } finally { setSaving(false); }
  };

  const reInvite = async (w) => {
    if (!w.email) { flash("error", "Add an email for this employee first."); return; }
    setInviting(w.id);
    setInviteLink(null);
    try {
      const m = membFor(w);
      const invite = await doInvite(w.email, w.id, m?.role || "field_employee");
      if (invite?.link) setInviteLink({ email: w.email, link: invite.link, emailed: invite.emailed });
      flash("success", invite?.emailed ? `Invite email sent to ${w.email} — link below as backup.` : `Invite link generated for ${w.email}.`);
      reload();
    } catch (e) { flash("error", e.message); } finally { setInviting(null); }
  };

  return (
    <Stack gap="3">
      <Flex justify="space-between" align="center" gap="2" flexWrap="wrap">
        <StatusSegments value={seg} onChange={setSeg} activeCount={activeEmployees.length}
          deactivatedCount={deactivatedEmployees.length} testPrefix="employees" />
        <HStack gap="2">
          <Button size="sm" variant="outline" onClick={() => setBulkOpen(true)} data-testid="setup-bulk-invite-btn">
            <Users size={15} /> Bulk invite
          </Button>
          <Button size="sm" onClick={() => setAddOpen(true)} data-testid="add-employee-btn">
            <Plus size={16} /> Add employee
          </Button>
        </HStack>
      </Flex>

      <BulkInviteModal open={bulkOpen} onClose={() => setBulkOpen(false)}
        companyId={companyId} flash={flash} onDone={reload} />

      {inviteLink && (
        <Card testId="invite-link-card">
          <Text fontSize="sm" fontWeight="600" color={colors.textPrimary} mb="1">Invite link for {inviteLink.email}{inviteLink.emailed ? " (email sent)" : ""}</Text>
          <Text fontSize="xs" color={colors.textSecondary} mb="2">
            {inviteLink.emailed
              ? "An invite email was sent. While email is in sandbox mode it may only reach verified inboxes — this link always works as backup."
              : "Send this link to the employee so they can set a password and log in. On first login they move from Pending Users into Employees automatically."}
          </Text>
          <Input value={inviteLink.link} readOnly onFocus={(e) => e.target.select()} {...inputStyle} data-testid="invite-link-input" fontSize="xs" />
        </Card>
      )}

      {shown.length === 0 ? (
        <Card testId="employees-empty">
          <Text color={colors.textSecondary} fontSize="sm">
            {seg === "active"
              ? "No active employees yet. Add an employee — they appear here once they log in for the first time."
              : "No deactivated employees."}
          </Text>
        </Card>
      ) : (
        shown.map((w) => {
          const m = membFor(w);
          const locCount = workerLocs.filter((x) => x.worker_id === w.id).length;
          // Unified status: the worker record drives Active/Deactivated everywhere.
          const statusMeta = w.status === "inactive"
            ? { bg: colors.pendingBg, color: colors.pendingText, label: "Deactivated" }
            : !m
              ? { bg: colors.cream, color: colors.textSecondary, label: "Not invited" }
              : { bg: colors.resolvedBg, color: colors.resolvedText, label: "Active" };
          return (
            <Card key={w.id} testId={`employee-row-${w.id}`}>
              <Flex justify="space-between" align="flex-start" gap="3" flexWrap="wrap">
                <Box>
                  <HStack gap="2" flexWrap="wrap">
                    <Text fontWeight="600" color={colors.textPrimary}>{w.full_name}</Text>
                    <Badge bg={statusMeta.bg} color={statusMeta.color} borderRadius="full" px="2.5">{statusMeta.label}</Badge>
                    {m && <Badge bg={colors.tealTint} color={colors.tealHover} borderRadius="full" px="2.5">{EMP_ROLE_LABELS[m.role] || m.role}</Badge>}
                  </HStack>
                  {w.job_role && <Text fontSize="sm" color={colors.textSecondary}>{w.job_role}</Text>}
                  {(() => {
                    // Linked worker: show the login email (source of truth), never workers.email.
                    const shownEmail = w.profile_id ? profilesById[w.profile_id]?.email : w.email;
                    return shownEmail ? <Text fontSize="sm" color={colors.textSecondary}>{shownEmail}</Text> : null;
                  })()}
                  <Text fontSize="xs" color={colors.textSecondary} mt="1">{locCount} location{locCount === 1 ? "" : "s"} assigned</Text>
                  {w.status === "inactive" && deactInfo[w.id] && (
                    <Text fontSize="xs" color={colors.textSecondary} data-testid={`deact-info-${w.id}`}>
                      Deactivated {deactInfo[w.id].when}{deactInfo[w.id].by ? ` by ${deactInfo[w.id].by}` : ""}
                    </Text>
                  )}
                </Box>
                <HStack gap="2">
                  {w.status === "inactive" ? (
                    <>
                      <Button size="xs" variant="outline" color={colors.sageDeep} onClick={() => reactivate(w)}
                        loading={reactivating === w.id} data-testid={`reactivate-employee-${w.id}`}>
                        Reactivate
                      </Button>
                      {deletable.has(w.id) && (
                        <Button size="xs" variant="outline" color={colors.poppy} onClick={() => setDelEmp(w)} data-testid={`delete-employee-${w.id}`}>
                          <Trash2 size={13} /> Delete
                        </Button>
                      )}
                    </>
                  ) : m ? (
                    <Button size="xs" variant="outline" onClick={() => setManage(w)} data-testid={`manage-employee-${w.id}`}>
                      <Settings2 size={14} /> Manage
                    </Button>
                  ) : (
                    <>
                      <Button size="xs" onClick={() => reInvite(w)} loading={inviting === w.id} data-testid={`invite-employee-${w.id}`}>
                        <Mail size={14} /> Invite to log in
                      </Button>
                      <Button size="xs" variant="outline" onClick={() => setManage(w)} data-testid={`manage-employee-${w.id}`}>
                        <Settings2 size={14} /> Manage
                      </Button>
                      {deletable.has(w.id) && (
                        <Button size="xs" variant="outline" color={colors.poppy} onClick={() => setDelEmp(w)} data-testid={`delete-employee-${w.id}`}>
                          <Trash2 size={13} />
                        </Button>
                      )}
                    </>
                  )}
                </HStack>
              </Flex>
            </Card>
          );
        })
      )}

      <Modal open={addOpen} onClose={closeAdd} title="Add employee" testId="employee-modal">
        <Stack gap="4">
          <FieldRow label="Full name" value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} data-testid="employee-name-input" />
          <FieldRow label="Job title / trade (optional)" value={form.job_role} onChange={(e) => setForm({ ...form, job_role: e.target.value })} data-testid="employee-jobrole-input" />
          <FieldRow label="Email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="employee-email-input" />
          <FieldRow label="Mobile phone (optional — for SMS alerts, e.g. +19025551234)" type="tel" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="employee-phone-input" />
          <Box>
            <Text fontSize="sm" fontWeight="500" mb="1.5" color={colors.textPrimary}>Role</Text>
            <RoleSelect value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} testId="employee-role-select" />
          </Box>
          {addError && (
            <Text fontSize="sm" color={colors.poppy} data-testid="employee-inline-error">{addError}</Text>
          )}
          <Button onClick={addEmployee} loading={saving} disabled={!form.full_name || !form.email} data-testid="save-employee-btn">
            Add & generate invite
          </Button>
        </Stack>
      </Modal>

      {manage && (
        <ManageEmployeeModal
          worker={manage}
          membership={membFor(manage)}
          loginEmail={manage.profile_id ? profilesById[manage.profile_id]?.email : null}
          allLocations={locations}
          allWorkers={workers}
          assignedLocationIds={managedLocIds}
          supervisedIds={managedSupIds}
          onClose={() => setManage(null)}
          reload={reload}
          flash={flash}
        />
      )}

      <ConfirmModal open={!!delEmp} onClose={() => setDelEmp(null)}
        title={delEmp ? `Permanently delete ${delEmp.full_name}?` : ""}
        body="They have no submissions, sign-offs or other history, so their worker record and login can be removed together. This cannot be undone."
        confirmLabel="Delete" danger busy={deleting} onConfirm={deleteEmp} testId="employee-delete-modal" />
    </Stack>
  );
}

function ManageEmployeeModal({ worker, membership, loginEmail, allLocations, allWorkers, assignedLocationIds, supervisedIds, onClose, reload, flash }) {
  // Linked to a login: the login email is the truth — never edit or write workers.email.
  const linked = !!worker.profile_id;
  const [form, setForm] = useState(linked
    ? { full_name: worker.full_name || "", job_role: worker.job_role || "", phone: worker.phone || "" }
    : { full_name: worker.full_name || "", job_role: worker.job_role || "", email: worker.email || "", phone: worker.phone || "" });
  const [role, setRole] = useState(membership?.role || "field_employee");
  const [locIds, setLocIds] = useState(new Set(assignedLocationIds));
  const [supIds, setSupIds] = useState(new Set(supervisedIds));
  const [saving, setSaving] = useState(false);
  const [confirmDeact, setConfirmDeact] = useState(false);
  const [saveError, setSaveError] = useState(null);

  const toggle = (set, setter, id) => {
    const next = new Set(set);
    next.has(id) ? next.delete(id) : next.add(id);
    setter(next);
  };

  const save = async () => {
    setSaving(true);
    setSaveError(null);
    try {
      const { error: we } = await supabase.from("workers").update(form).eq("id", worker.id);
      if (we) throw we;
      if (membership) {
        const { error: me } = await supabase.from("memberships").update({ role }).eq("id", membership.id);
        if (me) throw me;
      }
      // sync location assignments
      await supabase.from("worker_locations").delete().eq("worker_id", worker.id);
      const locRows = [...locIds].map((location_id) => ({ worker_id: worker.id, location_id }));
      if (locRows.length) {
        const { error: le } = await supabase.from("worker_locations").insert(locRows);
        if (le) throw le;
      }
      // sync supervised employees (only meaningful for supervisors)
      await supabase.from("supervisor_workers").delete().eq("supervisor_id", worker.id);
      if (role === "supervisor") {
        const supRows = [...supIds].filter((id) => id !== worker.id).map((wid) => ({ supervisor_id: worker.id, worker_id: wid }));
        if (supRows.length) {
          const { error: se } = await supabase.from("supervisor_workers").insert(supRows);
          if (se) throw se;
        }
      }
      flash("success", `${form.full_name} updated.`);
      onClose();
      reload();
    } catch (e) {
      // Inline, non-dismissing while the modal is open.
      setSaveError(e.code === "23505" || (e.message || "").includes("uq_workers_company_email")
        ? "Another employee in this company already has this email."
        : e.message);
    } finally { setSaving(false); }
  };

  // One source of truth: the server flips worker + membership atomically.
  const deactivate = async () => {
    setSaving(true);
    try {
      await apiPost(`/people/${worker.id}/status`, { active: false });
      flash("success", `${worker.full_name} deactivated — removed from assignment pickers.`);
      onClose();
      reload();
    } catch (e) { flash("error", e.message); } finally { setSaving(false); setConfirmDeact(false); }
  };

  const others = allWorkers.filter((w) => w.id !== worker.id);

  return (
    <Modal open onClose={onClose} title={`Manage ${worker.full_name}`} testId="manage-employee-modal">
      <Stack gap="4">
        <FieldRow label="Full name" value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} data-testid="manage-name-input" />
        <FieldRow label="Job title / trade" value={form.job_role} onChange={(e) => setForm({ ...form, job_role: e.target.value })} data-testid="manage-jobrole-input" />
        {linked ? (
          <Box data-testid="manage-login-email">
            <Text fontSize="sm" fontWeight="500" mb="1" color={colors.textPrimary}>Email</Text>
            <Text fontSize="sm" color={colors.textPrimary}>{loginEmail || "—"}</Text>
            <Text fontSize="xs" color={colors.textSecondary} mt="0.5">This is the address they sign in with — it comes from their account and can't be edited here.</Text>
          </Box>
        ) : (
          <FieldRow label="Email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="manage-email-input" />
        )}
        <FieldRow label="Mobile phone (optional — for SMS alerts, e.g. +19025551234)" type="tel" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="manage-phone-input" />

        {membership && (
          <Box>
            <Text fontSize="sm" fontWeight="500" mb="1.5" color={colors.textPrimary}>Role</Text>
            <RoleSelect value={role} onChange={(e) => setRole(e.target.value)} testId="manage-role-select" />
          </Box>
        )}

        <Box>
          <Text fontSize="sm" fontWeight="500" mb="1.5" color={colors.textPrimary}>Assigned locations</Text>
          {allLocations.length === 0 ? (
            <Text fontSize="xs" color={colors.textSecondary}>No locations yet. Add locations first.</Text>
          ) : (
            <Wrap gap="2" data-testid="manage-locations">
              {allLocations.map((l) => (
                <Pill key={l.id} active={locIds.has(l.id)} onClick={() => toggle(locIds, setLocIds, l.id)} testId={`loc-pill-${l.id}`}>{l.name}</Pill>
              ))}
            </Wrap>
          )}
        </Box>

        {role === "supervisor" && (
          <Box>
            <Text fontSize="sm" fontWeight="500" mb="1.5" color={colors.textPrimary}>Employees supervised</Text>
            {others.length === 0 ? (
              <Text fontSize="xs" color={colors.textSecondary}>No other employees to assign.</Text>
            ) : (
              <WorkerPicker workers={others} multi value={[...supIds]}
                onChange={(ids) => setSupIds(new Set(ids))}
                placeholder="Select employees" testId="manage-supervised" />
            )}
          </Box>
        )}

        {saveError && (
          <Text fontSize="sm" color={colors.poppy} data-testid="manage-inline-error">{saveError}</Text>
        )}
        <Flex gap="2" pt="1">
          <Button flex="1" onClick={save} loading={saving} data-testid="manage-save-btn">Save changes</Button>
          {membership && (
            <Button variant="outline" color={colors.poppy} onClick={() => setConfirmDeact(true)} loading={saving} data-testid="manage-toggle-status-btn">
              Deactivate
            </Button>
          )}
        </Flex>
      </Stack>

      <ConfirmModal open={confirmDeact} onClose={() => setConfirmDeact(false)}
        title={`Deactivate ${worker.full_name}?`}
        body="They immediately lose access and disappear from assignment and signer pickers everywhere. Their history is kept — reactivate them at any time from the Deactivated list."
        confirmLabel="Deactivate" danger busy={saving} onConfirm={deactivate} testId="employee-confirm-modal" />
    </Modal>
  );
}

// ---------------- Pending Users ----------------
function PendingTab({ pending, reload, flash }) {
  const [roleSel, setRoleSel] = useState({});
  const [busy, setBusy] = useState(null);

  const activate = async (m) => {
    const role = roleSel[m.id] || m.role || "field_employee";
    setBusy(m.id);
    try {
      const { error } = await supabase.from("memberships").update({ role, status: "active" }).eq("id", m.id);
      if (error) throw error;
      // Keep the unified person status in step — the worker record becomes assignable too.
      if (m.worker) {
        const { error: we } = await supabase.from("workers").update({ status: "active" }).eq("id", m.worker.id);
        if (we) throw we;
      }
      flash("success", `${m.profile?.full_name || m.profile?.email} activated as ${EMP_ROLE_LABELS[role]}.`);
      reload();
    } catch (e) { flash("error", e.message); } finally { setBusy(null); }
  };

  if (pending.length === 0) {
    return <Card testId="pending-empty"><Text color={colors.textSecondary} fontSize="sm">No users are pending first login right now. Invited employees appear here until they set a password and log in.</Text></Card>;
  }

  return (
    <Stack gap="3">
      {pending.map((m) => (
        <Card key={m.id} testId={`pending-row-${m.id}`}>
          <Flex justify="space-between" align="center" gap="3" flexWrap="wrap">
            <Box>
              <HStack gap="2" flexWrap="wrap">
                <Text fontWeight="600" color={colors.textPrimary}>{m.worker?.full_name || m.profile?.full_name || "(no name)"}</Text>
                <Badge bg={colors.tealTint} color={colors.tealHover} borderRadius="full" px="2.5">{EMP_ROLE_LABELS[m.role] || m.role}</Badge>
              </HStack>
              <Text fontSize="sm" color={colors.textSecondary}>{m.profile?.email}</Text>
              <Text fontSize="xs" color={colors.textSecondary} mt="1">Awaiting first login — will move to Employees automatically.</Text>
            </Box>
            <HStack gap="2">
              <Box as="select" value={roleSel[m.id] || m.role || "field_employee"}
                onChange={(e) => setRoleSel({ ...roleSel, [m.id]: e.target.value })}
                data-testid={`role-select-${m.id}`}
                px="3" py="2" borderRadius="8px" border="1px solid" borderColor={colors.border} bg={colors.white} fontSize="sm">
                {INVITE_ROLES.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
              </Box>
              <Button size="sm" onClick={() => activate(m)} loading={busy === m.id} data-testid={`activate-${m.id}`}>
                <CheckCircle2 size={16} /> Activate
              </Button>
            </HStack>
          </Flex>
        </Card>
      ))}
    </Stack>
  );
}
